/**
 * Header for builder mode — the one place a builder chat offers a way out.
 *
 * Rendered on BOTH builder routes, including `/build` before a conversation
 * exists. That matters: entering the builder and changing your mind before
 * typing anything used to leave you with no way back except the browser
 * button, because the pre-conversation route had no header at all.
 *
 * The destination follows WHAT is being built — an automation builder returns
 * to the automations list, a validation-rule builder to settings. See
 * `getBuilderBackDestination` for why that beats remembering where the user
 * came from.
 */

import { ArrowLeft } from '@untitledui/icons';
import type { ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/lib/base/buttons/button';
import { isValidEntityTypeName } from '@/lib/crm/core/types';
import { useCrmObjectSnapshot } from '@/lib/crm/hooks/useCrmObjectSnapshot';
import { useEntityTypeSchema } from '@/lib/crm/hooks/useEntityTypeSchema';
import { getDisplayName } from '@/lib/crm/utils/getDisplayName';
import { getEntityKindLabel } from '@/lib/crm/utils/getEntityKindLabel';
import { getTypeLabelByName } from '@/lib/crm/utils/getTypeLabel';
import { getBuilderBackDestination } from '@/lib/reasoning/builderBackDestination';

export interface BuilderChatHeaderProps {
  orgId: string | number | undefined;
  /**
   * The kind of record being built. Drives both the destination and the
   * breadcrumb, so a validation-rule builder never offers "back to
   * automations".
   */
  entityTypeName: string | null | undefined;
  /**
   * The record being edited, when there is one. Drives the "Editing X" title
   * — the header resolves the display name itself rather than making every
   * host thread it through.
   */
  entityId?: string;
  /**
   * Org the record belongs to, for the store lookup. Separate from `orgId`
   * only because that one may be a route string.
   */
  lookupOrgId?: number;
  /**
   * Conversation title. Absent before the first message — the header still
   * renders, showing only the section name, so the back affordance exists from
   * the moment the route does.
   */
  title?: string | null;
  /** Optional trailing content (actions). */
  children?: ReactNode;
}

export function BuilderChatHeader({
  orgId,
  entityTypeName,
  entityId,
  lookupOrgId,
  title,
  children,
}: BuilderChatHeaderProps) {
  const navigate = useNavigate();
  // Store-first; `undefined` until it resolves, which the title handles.
  const savedRecord = useCrmObjectSnapshot(entityId, lookupOrgId);

  const typeForSchema =
    entityTypeName && isValidEntityTypeName(entityTypeName)
      ? entityTypeName
      : undefined;
  const { schema: entityTypeSchema } = useEntityTypeSchema(
    lookupOrgId ?? orgId,
    typeForSchema
  );
  const kindLabel = getEntityKindLabel(
    entityTypeSchema?.entity_kind ?? undefined
  );

  const destination = useMemo(
    () => getBuilderBackDestination(orgId, entityTypeName),
    [orgId, entityTypeName]
  );

  const handleBack = useCallback(() => {
    if (!destination) {
      return;
    }
    navigate(destination.path);
  }, [navigate, destination]);

  // The title states what the user is DOING, not what the conversation is
  // called. Editing an existing record and creating one from nothing are
  // different jobs, and on mobile the panel is not on screen — so if the
  // header does not name the record, nothing does.
  const displayTitle = useMemo(() => {
    const typeLabel = entityTypeName
      ? getTypeLabelByName(entityTypeName).toLowerCase()
      : 'record';

    if (savedRecord) {
      // `getDisplayName` falls back to the type label when the record has no
      // name yet, so this never degrades to "Editing undefined".
      return `Editing ${getDisplayName(savedRecord, typeLabel)}`;
    }
    // An edit conversation whose record has not loaded yet — say what is
    // happening rather than briefly claiming it is new.
    if (entityId) {
      return `Editing ${typeLabel}`;
    }
    if (title) {
      return title;
    }
    return `New ${kindLabel ? kindLabel.toLowerCase() : typeLabel}`;
  }, [title, entityTypeName, savedRecord, entityId, kindLabel]);

  return (
    <div className='bg-bg-primary border-b border-border-secondary'>
      <div className='flex items-center justify-between px-3 sm:px-6 py-4'>
        <div className='flex min-w-0 items-center gap-2'>
          {destination && (
            <Button
              color='tertiary'
              onClick={handleBack}
              // Names the destination rather than saying "Back": with no
              // visible label this is all a screen reader announces.
              aria-label={`Back to ${destination.label.toLowerCase()}`}
              className='!p-1.5 -ml-1.5'
              iconLeading={
                <ArrowLeft className='size-5 text-tertiary' strokeWidth={2} />
              }
            />
          )}
          <p className='truncate text-base leading-5 text-black dark:text-white'>
            {destination && (
              <span className='hidden font-normal text-neutral-600 sm:inline dark:text-neutral-400'>
                {destination.label} /{' '}
              </span>
            )}
            <span className='font-medium'>{displayTitle}</span>
          </p>
        </div>
        {children}
      </div>
    </div>
  );
}
